import React, { useState, useEffect, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion, useScroll, useTransform, AnimatePresence } from 'framer-motion';
import {
  ArrowRight, Brain, Target, Map, Rocket, Check, Sparkles, Zap, Crown, Star,
  Users, TrendingUp, FileText, ChevronDown, ChevronUp, CheckCircle2,
  Award, Shield, Clock, BarChart3, GraduationCap, Building2,
  Play, Quote, Flame, BadgeCheck, Wand2, Eye, Download, Briefcase, Code2,
  PieChart, ArrowUpRight, BookOpen, Terminal, Cpu, Layers, GitBranch,
  Crosshair, Activity, Globe, Compass, MonitorSmartphone
} from 'lucide-react';
import SEO, { seoConfigs } from '../components/SEO';
import authService from '../firebase/authService';
import { blogAPI } from '../utils/api';

/* ─── Animated counter ─────────────────────────────────────────────────── */
const AnimNumber = ({ value, suffix = '' }) => {
  const [count, setCount] = useState(0);
  const ref = useRef(null);
  useEffect(() => {
    const observer = new IntersectionObserver(([e]) => {
      if (e.isIntersecting) {
        let start = 0;
        const end = value;
        const timer = setInterval(() => {
          start += end / 40;
          if (start >= end) { setCount(end); clearInterval(timer); }
          else setCount(Math.floor(start));
        }, 30);
      }
    });
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, [value]);
  return <span ref={ref}>{count.toLocaleString()}{suffix}</span>;
};

/* ─── FAQ ──────────────────────────────────────────────────────────────── */
const FAQ = ({ items }) => {
  const [open, setOpen] = useState(0);
  return (
    <div className="space-y-3">
      {items.map((item, i) => (
        <div key={i} className="border border-white/10 rounded-2xl overflow-hidden bg-white/[0.015]">
          <button onClick={() => setOpen(open === i ? -1 : i)} className="w-full px-6 py-5 flex justify-between items-center hover:bg-white/[0.03] transition-colors text-left">
            <span className="font-semibold text-white text-[15px]">{item.q}</span>
            <ChevronDown className={`w-4 h-4 text-emerald-400 transition-transform shrink-0 ml-4 ${open === i ? 'rotate-180' : ''}`} />
          </button>
          <AnimatePresence>
            {open === i && (
              <motion.div initial={{ height: 0 }} animate={{ height: 'auto' }} exit={{ height: 0 }} className="overflow-hidden">
                <p className="px-6 pb-5 text-slate-400 text-sm leading-relaxed">{item.a}</p>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      ))}
    </div>
  );
};

/* ═══════════════════════════════════════════════════════════════════════ */
const HomePage = () => {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [blogPosts, setBlogPosts] = useState([]);
  const heroRef = useRef(null);
  const { scrollYProgress } = useScroll({ target: heroRef, offset: ['start start', 'end start'] });
  const heroOpacity = useTransform(scrollYProgress, [0, 1], [1, 0]);
  const heroY = useTransform(scrollYProgress, [0, 1], [0, 120]);

  useEffect(() => { const unsub = authService.onAuthStateChange(setUser); return () => unsub(); }, []);
  useEffect(() => {
    (async () => {
      try { const d = await blogAPI.getAllPosts(1, 3); setBlogPosts(d.posts || []); } catch { }
    })();
  }, []);

  const faqs = [
    { q: "Is Rastor AI really 100% free?", a: "Yes! Our core AI career coach, personalized roadmaps, and 2 free resume downloads are completely free. No credit card required. Premium plans unlock unlimited access." },
    { q: "How is this different from ₹2 lakh coaching programs?", a: "We use AI to deliver personalized, data-driven guidance instantly. No generic advice. No waiting weeks. Real salary data. All at a fraction of the cost - or completely free." },
    { q: "What makes the resume builder special?", a: "Our AI optimizes your resume for ATS systems that filter 75% of applications. Plus, paste any job description and we'll tailor your resume specifically for that role." },
    { q: "How accurate are the salary insights?", a: "We aggregate data from multiple sources including real job postings. City-specific insights for Bangalore, Mumbai, Delhi, Hyderabad, and more." },
    { q: "Can I use this for a career switch?", a: "Absolutely! We specialize in career transitions. Our AI identifies transferable skills and creates bridge roadmaps to your new career." },
    { q: "How fast can I get my roadmap?", a: "2 minutes. Answer a few questions about your background, and our AI generates a personalized week-by-week learning plan instantly." }
  ];

  return (
    <div className="bg-[#08090c] min-h-screen text-white overflow-x-hidden">
      <SEO {...seoConfigs.home} />

      {/* ═══ AMBIENT BG ═══ */}
      <div className="fixed inset-0 pointer-events-none z-0">
        <div className="absolute top-0 left-1/4 w-[900px] h-[900px] bg-gradient-to-br from-emerald-600/[0.12] via-teal-500/[0.06] to-transparent rounded-full blur-[200px]" />
        <div className="absolute bottom-0 right-0 w-[700px] h-[700px] bg-gradient-to-bl from-cyan-500/[0.08] via-blue-500/[0.04] to-transparent rounded-full blur-[180px]" />
        <div className="absolute inset-0" style={{
          backgroundImage: `linear-gradient(rgba(255,255,255,0.015) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.015) 1px, transparent 1px)`,
          backgroundSize: '80px 80px'
        }} />
      </div>

      {/* ═══════════════════════════════════════════════════════════════════
           HERO — Command centre style
       ═══════════════════════════════════════════════════════════════════ */}
      <section ref={heroRef} className="relative min-h-[92vh] flex items-center px-4 pt-28 pb-20 z-10">
        <motion.div style={{ opacity: heroOpacity, y: heroY }} className="max-w-7xl mx-auto w-full">
          {/* Status bar */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="flex items-center gap-3 mb-8"
          >
            <span className="relative flex h-2 w-2">
              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75" />
              <span className="relative inline-flex rounded-full h-2 w-2 bg-emerald-500" />
            </span>
            <span className="text-xs font-mono text-emerald-400/80 uppercase tracking-widest">System Online · AI Models Ready</span>
          </motion.div>

          <div className="grid lg:grid-cols-12 gap-12 items-center">
            {/* Left — 7 cols */}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7 }}
              className="lg:col-span-7"
            >
              <h1 className="text-[3.2rem] md:text-[4rem] lg:text-[4.8rem] font-extrabold leading-[1.05] tracking-tight mb-6">
                Engineer Your
                <br />
                <span className="text-transparent bg-clip-text bg-gradient-to-r from-emerald-400 via-teal-400 to-cyan-400">
                  Career Trajectory
                </span>
              </h1>

              <p className="text-lg md:text-xl text-slate-400 mb-10 max-w-xl leading-relaxed">
                AI-powered roadmaps, salary intelligence, and ATS-beating resumes.
                Get a <span className="text-white font-medium">personalized career plan in 2 minutes</span> — not 2 months.
              </p>

              <div className="flex flex-col sm:flex-row gap-4 mb-10">
                <motion.button
                  whileHover={{ scale: 1.04, boxShadow: '0 0 40px rgba(16,185,129,0.3)' }}
                  whileTap={{ scale: 0.97 }}
                  onClick={() => navigate('/build-roadmap')}
                  className="group px-8 py-4 bg-gradient-to-r from-emerald-500 to-teal-500 text-white font-bold text-lg rounded-2xl shadow-xl shadow-emerald-500/20 flex items-center justify-center gap-3"
                >
                  <Rocket className="w-5 h-5" />
                  <span>Deploy My Roadmap</span>
                  <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
                </motion.button>
                <Link
                  to="/resume-builder"
                  className="px-8 py-4 bg-white/[0.04] border border-white/[0.12] rounded-2xl font-semibold flex items-center justify-center gap-2 hover:bg-white/[0.08] hover:border-white/20 transition-all"
                >
                  <FileText className="w-5 h-5 text-violet-400" />
                  <span>Build ATS Resume</span>
                </Link>
              </div>

              <div className="flex flex-wrap gap-6 text-[13px] text-slate-500">
                <span className="flex items-center gap-2"><CheckCircle2 className="w-4 h-4 text-emerald-500" />100% Free to start</span>
                <span className="flex items-center gap-2"><CheckCircle2 className="w-4 h-4 text-emerald-500" />No credit card</span>
                <span className="flex items-center gap-2"><CheckCircle2 className="w-4 h-4 text-emerald-500" />2 min setup</span>
              </div>
            </motion.div>

            {/* Right — 5 cols: mini dashboard preview */}
            <motion.div
              initial={{ opacity: 0, y: 40 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, delay: 0.25 }}
              className="lg:col-span-5 hidden lg:block"
            >
              <div className="relative">
                {/* Main card */}
                <div className="bg-[#0d0f14] border border-white/[0.08] rounded-2xl p-6 shadow-2xl shadow-black/40">
                  <div className="flex items-center justify-between mb-5">
                    <div className="flex items-center gap-3">
                      <div className="w-9 h-9 bg-emerald-500/10 border border-emerald-500/20 rounded-xl flex items-center justify-center">
                        <Terminal className="w-4 h-4 text-emerald-400" />
                      </div>
                      <div>
                        <div className="text-xs font-mono text-slate-500 uppercase">Career Roadmap</div>
                        <div className="text-sm font-bold text-white">Senior Software Engineer</div>
                      </div>
                    </div>
                    <span className="text-[10px] font-mono text-emerald-400 bg-emerald-500/10 px-2 py-0.5 rounded">94% MATCH</span>
                  </div>

                  {/* Progress items */}
                  <div className="space-y-2.5 mb-5">
                    {[
                      { label: 'Foundation Skills', done: true, week: 'W1-2' },
                      { label: 'Core Projects', done: true, week: 'W3-4' },
                      { label: 'System Design', done: false, active: true, week: 'W5-6' },
                      { label: 'Interview Prep', done: false, week: 'W7-8' },
                    ].map((item, i) => (
                      <div key={i} className={`flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm ${item.active ? 'bg-emerald-500/[0.08] border border-emerald-500/20' :
                          item.done ? 'bg-white/[0.03]' : 'bg-white/[0.015]'
                        }`}>
                        <div className={`w-5 h-5 rounded-full flex items-center justify-center text-[10px] font-bold ${item.done ? 'bg-emerald-500 text-white' :
                            item.active ? 'border-2 border-emerald-500 text-emerald-400' : 'bg-white/10 text-slate-500'
                          }`}>
                          {item.done ? <Check className="w-3 h-3" /> : i + 1}
                        </div>
                        <span className={item.done ? 'text-slate-300' : item.active ? 'text-white font-medium' : 'text-slate-500'}>{item.label}</span>
                        <span className="ml-auto text-[10px] font-mono text-slate-600">{item.week}</span>
                      </div>
                    ))}
                  </div>

                  {/* Salary bar */}
                  <div className="bg-white/[0.03] rounded-xl p-3">
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-[10px] font-mono text-slate-500 uppercase">Projected Salary Range</span>
                      <span className="text-[10px] font-mono text-emerald-400">+40%</span>
                    </div>
                    <div className="flex items-center gap-3">
                      <div className="flex-1 h-2 bg-white/[0.06] rounded-full overflow-hidden">
                        <motion.div
                          className="h-full rounded-full bg-gradient-to-r from-emerald-500 to-teal-400"
                          initial={{ width: '0%' }}
                          animate={{ width: '72%' }}
                          transition={{ duration: 1.5, delay: 0.8 }}
                        />
                      </div>
                      <span className="text-xs font-mono text-white font-bold">₹25-45L</span>
                    </div>
                  </div>
                </div>

                {/* Floating badges */}
                <motion.div
                  animate={{ y: [0, -8, 0] }}
                  transition={{ duration: 3, repeat: Infinity, ease: 'easeInOut' }}
                  className="absolute -top-4 -right-4 px-3 py-2 bg-[#0d0f14] border border-emerald-500/20 rounded-xl shadow-lg"
                >
                  <div className="flex items-center gap-2 text-emerald-400 text-xs font-mono font-bold">
                    <TrendingUp className="w-3.5 h-3.5" />
                    <span>+40% Avg Hike</span>
                  </div>
                </motion.div>
                <motion.div
                  animate={{ y: [0, 8, 0] }}
                  transition={{ duration: 4, repeat: Infinity, ease: 'easeInOut' }}
                  className="absolute -bottom-4 -left-4 px-3 py-2 bg-[#0d0f14] border border-yellow-500/20 rounded-xl shadow-lg"
                >
                  <div className="flex items-center gap-2 text-yellow-400 text-xs font-mono font-bold">
                    <Star className="w-3.5 h-3.5 fill-current" />
                    <span>4.9/5 Rating</span>
                  </div>
                </motion.div>
              </div>
            </motion.div>
          </div>
        </motion.div>
      </section>

      {/* ═══ METRICS RIBBON ═══ */}
      <section className="relative py-10 border-y border-white/[0.06] bg-white/[0.01] z-10">
        <div className="max-w-6xl mx-auto px-4">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6 text-center">
            {[
              { val: 10847, suffix: '+', label: 'Professionals' },
              { val: 100, suffix: '+', label: 'Career Paths' },
              { val: 40, suffix: '%', label: 'Avg Salary Hike' },
              { val: 2, suffix: ' min', label: 'To Get Roadmap' },
            ].map((s, i) => (
              <div key={i}>
                <div className="text-3xl md:text-4xl font-extrabold text-white mb-1 font-mono tabular-nums"><AnimNumber value={s.val} suffix={s.suffix} /></div>
                <div className="text-xs text-slate-500 uppercase tracking-wider font-mono">{s.label}</div>
              </div>
            ))}
          </div>

          <div className="mt-8 pt-6 border-t border-white/[0.05]">
            <p className="text-center text-[10px] text-slate-600 uppercase tracking-[0.2em] mb-5 font-mono">Trusted by professionals from</p>
            <div className="flex items-center justify-center gap-8 md:gap-14 flex-wrap opacity-30">
              {['Google', 'Amazon', 'Microsoft', 'Flipkart', 'Razorpay', 'Swiggy'].map((c, i) => (
                <span key={i} className="text-slate-400 font-bold text-base tracking-wide">{c}</span>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ═══════════════════════════════════════════════════════════════════
           BENTO FEATURE GRID — 4 features in a 2×2 bento layout
       ═══════════════════════════════════════════════════════════════════ */}
      <section className="relative py-24 px-4 z-10">
        <div className="max-w-7xl mx-auto">
          <motion.div initial={{ opacity: 0 }} whileInView={{ opacity: 1 }} viewport={{ once: true }} className="text-center mb-16">
            <span className="inline-flex items-center gap-2 px-4 py-2 bg-emerald-500/10 border border-emerald-500/20 rounded-full text-emerald-400 text-xs font-mono uppercase tracking-wider mb-6">
              <Cpu className="w-3.5 h-3.5" />
              Core Modules
            </span>
            <h2 className="text-4xl md:text-5xl lg:text-6xl font-extrabold tracking-tight mb-5">
              Everything You Need to
              <br />
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-emerald-400 to-cyan-400">Level Up Your Career</span>
            </h2>
            <p className="text-lg text-slate-400 max-w-2xl mx-auto">
              Four battle-tested modules working together to engineer your next career move.
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 gap-5">
            {/* Feature 1: AI Career Coach — large */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="group relative bg-[#0d0f14] border border-white/[0.06] rounded-2xl p-8 hover:border-emerald-500/20 transition-all duration-500 overflow-hidden"
            >
              <div className="absolute top-0 right-0 w-64 h-64 bg-emerald-500/[0.04] rounded-full blur-[80px] pointer-events-none group-hover:bg-emerald-500/[0.08] transition-colors duration-700" />
              <div className="relative z-10">
                <div className="flex items-center gap-3 mb-5">
                  <div className="w-10 h-10 bg-emerald-500/10 border border-emerald-500/20 rounded-xl flex items-center justify-center">
                    <Brain className="w-5 h-5 text-emerald-400" />
                  </div>
                  <div>
                    <div className="font-bold text-white">AI Career Coach</div>
                    <div className="text-xs text-slate-500 font-mono">MODULE_01</div>
                  </div>
                </div>
                <p className="text-slate-400 text-sm mb-6 leading-relaxed max-w-md">
                  Analyzes your skills, experience, and goals to recommend career paths with the highest ROI — backed by real market data from 100+ career paths.
                </p>
                <div className="grid grid-cols-2 gap-3 mb-6">
                  {['Skill gap analysis', 'Salary projections', '100+ career paths', 'Growth forecasts'].map((item, i) => (
                    <div key={i} className="flex items-center gap-2 text-xs text-slate-300">
                      <div className="w-1 h-1 bg-emerald-500 rounded-full" />
                      {item}
                    </div>
                  ))}
                </div>
                <button onClick={() => navigate('/build-roadmap')} className="flex items-center gap-2 text-sm text-emerald-400 font-medium hover:text-emerald-300 transition-colors group/btn">
                  <span>Get AI Analysis</span>
                  <ArrowRight className="w-4 h-4 group-hover/btn:translate-x-1 transition-transform" />
                </button>
              </div>
            </motion.div>

            {/* Feature 2: Resume Builder */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.1 }}
              className="group relative bg-[#0d0f14] border border-white/[0.06] rounded-2xl p-8 hover:border-violet-500/20 transition-all duration-500 overflow-hidden"
            >
              <div className="absolute top-0 right-0 w-64 h-64 bg-violet-500/[0.04] rounded-full blur-[80px] pointer-events-none group-hover:bg-violet-500/[0.08] transition-colors duration-700" />
              <div className="relative z-10">
                <div className="flex items-center gap-3 mb-5">
                  <div className="w-10 h-10 bg-violet-500/10 border border-violet-500/20 rounded-xl flex items-center justify-center">
                    <FileText className="w-5 h-5 text-violet-400" />
                  </div>
                  <div className="flex-1">
                    <div className="font-bold text-white">ATS Resume Builder</div>
                    <div className="text-xs text-slate-500 font-mono">MODULE_02</div>
                  </div>
                  <span className="px-2 py-0.5 bg-emerald-500/10 border border-emerald-500/20 rounded text-[10px] font-mono font-bold text-emerald-400">FREE</span>
                </div>
                <p className="text-slate-400 text-sm mb-6 leading-relaxed max-w-md">
                  75% of resumes never make it past ATS bots. Our AI builder ensures yours does — with keyword optimization and job-specific tailoring.
                </p>
                <div className="grid grid-cols-2 gap-3 mb-6">
                  {['ATS-optimized format', 'AI keyword boost', 'Job-specific tailor', 'PDF download'].map((item, i) => (
                    <div key={i} className="flex items-center gap-2 text-xs text-slate-300">
                      <div className="w-1 h-1 bg-violet-500 rounded-full" />
                      {item}
                    </div>
                  ))}
                </div>
                <Link to="/resume-builder" className="flex items-center gap-2 text-sm text-violet-400 font-medium hover:text-violet-300 transition-colors group/btn">
                  <span>Build My Resume</span>
                  <ArrowRight className="w-4 h-4 group-hover/btn:translate-x-1 transition-transform" />
                </Link>
              </div>
            </motion.div>

            {/* Feature 3: Learning Roadmaps */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.2 }}
              className="group relative bg-[#0d0f14] border border-white/[0.06] rounded-2xl p-8 hover:border-blue-500/20 transition-all duration-500 overflow-hidden"
            >
              <div className="absolute top-0 right-0 w-64 h-64 bg-blue-500/[0.04] rounded-full blur-[80px] pointer-events-none group-hover:bg-blue-500/[0.08] transition-colors duration-700" />
              <div className="relative z-10">
                <div className="flex items-center gap-3 mb-5">
                  <div className="w-10 h-10 bg-blue-500/10 border border-blue-500/20 rounded-xl flex items-center justify-center">
                    <Map className="w-5 h-5 text-blue-400" />
                  </div>
                  <div>
                    <div className="font-bold text-white">Week-by-Week Roadmaps</div>
                    <div className="text-xs text-slate-500 font-mono">MODULE_03</div>
                  </div>
                </div>
                <p className="text-slate-400 text-sm mb-6 leading-relaxed max-w-md">
                  Stop wondering what to learn next. Get structured learning paths with tasks, curated resources, and milestone tracking — personalized to your pace.
                </p>
                <div className="grid grid-cols-2 gap-3 mb-6">
                  {['Your pace, your plan', 'Curated resources', 'Project-based', 'Progress tracking'].map((item, i) => (
                    <div key={i} className="flex items-center gap-2 text-xs text-slate-300">
                      <div className="w-1 h-1 bg-blue-500 rounded-full" />
                      {item}
                    </div>
                  ))}
                </div>
                <button onClick={() => navigate('/build-roadmap')} className="flex items-center gap-2 text-sm text-blue-400 font-medium hover:text-blue-300 transition-colors group/btn">
                  <span>Generate Roadmap</span>
                  <ArrowRight className="w-4 h-4 group-hover/btn:translate-x-1 transition-transform" />
                </button>
              </div>
            </motion.div>

            {/* Feature 4: Salary Intelligence */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.3 }}
              className="group relative bg-[#0d0f14] border border-white/[0.06] rounded-2xl p-8 hover:border-amber-500/20 transition-all duration-500 overflow-hidden"
            >
              <div className="absolute top-0 right-0 w-64 h-64 bg-amber-500/[0.04] rounded-full blur-[80px] pointer-events-none group-hover:bg-amber-500/[0.08] transition-colors duration-700" />
              <div className="relative z-10">
                <div className="flex items-center gap-3 mb-5">
                  <div className="w-10 h-10 bg-amber-500/10 border border-amber-500/20 rounded-xl flex items-center justify-center">
                    <BarChart3 className="w-5 h-5 text-amber-400" />
                  </div>
                  <div>
                    <div className="font-bold text-white">Salary Intelligence</div>
                    <div className="text-xs text-slate-500 font-mono">MODULE_04</div>
                  </div>
                </div>
                <p className="text-slate-400 text-sm mb-6 leading-relaxed max-w-md">
                  Know your worth. City-specific salary ranges, company comparisons, and growth projections to negotiate your next offer with confidence.
                </p>
                <div className="grid grid-cols-2 gap-3 mb-6">
                  {['City-specific data', 'Company breakdown', 'Experience levels', 'Growth trends'].map((item, i) => (
                    <div key={i} className="flex items-center gap-2 text-xs text-slate-300">
                      <div className="w-1 h-1 bg-amber-500 rounded-full" />
                      {item}
                    </div>
                  ))}
                </div>
                <button onClick={() => navigate('/build-roadmap')} className="flex items-center gap-2 text-sm text-amber-400 font-medium hover:text-amber-300 transition-colors group/btn">
                  <span>See Salary Data</span>
                  <ArrowRight className="w-4 h-4 group-hover/btn:translate-x-1 transition-transform" />
                </button>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* ═══ PROBLEM → SOLUTION SECTION ═══ */}
      <section className="relative py-24 px-4 z-10">
        <div className="max-w-6xl mx-auto">
          <motion.div initial={{ opacity: 0 }} whileInView={{ opacity: 1 }} viewport={{ once: true }} className="text-center mb-16">
            <span className="inline-flex items-center gap-2 px-4 py-2 bg-red-500/10 border border-red-500/20 rounded-full text-red-400 text-xs font-mono uppercase tracking-wider mb-6">
              <Flame className="w-3.5 h-3.5" />
              The Harsh Truth
            </span>
            <h2 className="text-4xl md:text-5xl lg:text-6xl font-extrabold tracking-tight mb-5">
              Why Spend <span className="text-red-400">₹2,00,000</span>
              <br />on Fake Career Programs?
            </h2>
            <p className="text-lg text-slate-400 max-w-3xl mx-auto">
              Career coaching in India is a ₹5000 crore industry built on selling dreams.
            </p>
          </motion.div>

          <div className="grid md:grid-cols-3 gap-5 mb-12">
            {[
              { icon: '₹', title: 'Overpriced Programs', desc: '₹50K - ₹2L for generic PDFs and group calls. Same advice given to thousands.', color: 'red' },
              { icon: Clock, title: 'Months of Waiting', desc: 'Wait weeks for a mentor who follows a script. No personalization, no real guidance.', color: 'red' },
              { icon: Shield, title: 'Zero Accountability', desc: "No refunds. No outcomes. If you fail, it's 'your fault' — not their outdated advice.", color: 'red' },
            ].map((item, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.1 }}
                className="p-6 bg-red-500/[0.03] border border-red-500/[0.12] rounded-2xl"
              >
                <div className="w-10 h-10 bg-red-500/10 border border-red-500/20 rounded-xl flex items-center justify-center mb-4">
                  {typeof item.icon === 'string'
                    ? <span className="text-lg font-bold text-red-400">{item.icon}</span>
                    : <item.icon className="w-5 h-5 text-red-400" />
                  }
                </div>
                <h3 className="text-lg font-bold text-white mb-2">{item.title}</h3>
                <p className="text-sm text-slate-400 leading-relaxed">{item.desc}</p>
              </motion.div>
            ))}
          </div>

          {/* Solution CTA */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="p-8 bg-gradient-to-r from-emerald-500/[0.06] via-transparent to-teal-500/[0.06] border border-emerald-500/[0.15] rounded-2xl text-center"
          >
            <h3 className="text-2xl font-bold text-white mb-3">There's a Smarter Way</h3>
            <p className="text-slate-400 mb-6 max-w-xl mx-auto text-sm">AI-powered career guidance that's personalized, instant, and costs nothing to start.</p>
            <motion.button
              whileHover={{ scale: 1.04 }}
              whileTap={{ scale: 0.97 }}
              onClick={() => navigate('/build-roadmap')}
              className="px-8 py-3.5 bg-gradient-to-r from-emerald-500 to-teal-500 text-white font-bold rounded-xl inline-flex items-center gap-2 shadow-lg shadow-emerald-500/20"
            >
              <span>Try Free — No Credit Card</span>
              <ArrowRight className="w-5 h-5" />
            </motion.button>
          </motion.div>
        </div>
      </section>

      {/* ═══ TESTIMONIALS ═══ */}
      <section className="relative py-24 px-4 z-10 border-t border-white/[0.05]">
        <div className="max-w-6xl mx-auto">
          <motion.div initial={{ opacity: 0 }} whileInView={{ opacity: 1 }} viewport={{ once: true }} className="text-center mb-16">
            <span className="inline-flex items-center gap-2 px-4 py-2 bg-yellow-500/10 border border-yellow-500/20 rounded-full text-yellow-400 text-xs font-mono uppercase tracking-wider mb-6">
              <Star className="w-3.5 h-3.5 fill-current" />
              4.9/5 Average Rating
            </span>
            <h2 className="text-4xl md:text-5xl font-extrabold tracking-tight mb-4">Real Stories, Real Results</h2>
            <p className="text-lg text-slate-400">Career transformations powered by Rastor AI</p>
          </motion.div>

          <div className="grid md:grid-cols-3 gap-5 mb-10">
            {[
              { name: "Priyanka S.", role: "SDE-2 @ Amazon", quote: "Rastor AI's roadmap was better than my ₹80K coaching program. Landed Amazon in 3 months!", avatar: "PS", hike: "₹18L → ₹32L", transition: "QA → SDE-2" },
              { name: "Rahul K.", role: "PM @ Flipkart", quote: "The salary insights helped me negotiate 45% higher. This tool is insane for interview prep.", avatar: "RK", hike: "₹22L → ₹38L", transition: "Dev → PM" },
              { name: "Ananya G.", role: "Data Scientist", quote: "Switched from marketing to data science. The AI tailor feature got me 5x more callbacks.", avatar: "AG", hike: "₹12L → ₹28L", transition: "Marketing → DS" },
              { name: "Vikram P.", role: "Cloud Architect @ TCS", quote: "The week-by-week roadmap kept me accountable. Cleared AWS SA in 6 weeks.", avatar: "VP", hike: "₹16L → ₹34L", transition: "Backend → Cloud" },
              { name: "Sneha M.", role: "UX Lead @ Razorpay", quote: "The portfolio builder helped me showcase my work properly. Got 3 offers in 2 weeks.", avatar: "SM", hike: "₹14L → ₹26L", transition: "Graphic → UX" },
              { name: "Arjun R.", role: "ML Engineer @ Google", quote: "Best career investment I've made. The AI coach answered questions my mentors couldn't.", avatar: "AR", hike: "₹20L → ₹45L", transition: "Research → ML" }
            ].map((t, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.06 }}
                className="p-6 bg-[#0d0f14] border border-white/[0.06] rounded-2xl hover:border-white/[0.12] transition-colors"
              >
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center gap-0.5">{[1, 2, 3, 4, 5].map(s => <Star key={s} className="w-3.5 h-3.5 text-yellow-400 fill-yellow-400" />)}</div>
                  <span className="flex items-center gap-1 text-[9px] text-emerald-400 bg-emerald-500/10 px-1.5 py-0.5 rounded font-mono"><CheckCircle2 className="w-2.5 h-2.5" />VERIFIED</span>
                </div>
                <p className="text-slate-300 text-sm mb-5 leading-relaxed">"{t.quote}"</p>
                <div className="flex items-center justify-between border-t border-white/[0.06] pt-4">
                  <div className="flex items-center gap-2.5">
                    <div className="w-9 h-9 bg-gradient-to-br from-emerald-400 to-teal-500 rounded-full flex items-center justify-center text-xs font-bold">{t.avatar}</div>
                    <div>
                      <div className="font-semibold text-white text-sm">{t.name}</div>
                      <div className="text-[11px] text-slate-500">{t.role}</div>
                    </div>
                  </div>
                  <div className="text-right">
                    <div className="text-emerald-400 font-bold text-xs font-mono">{t.hike}</div>
                    <div className="text-[10px] text-slate-600">{t.transition}</div>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>

          <motion.div initial={{ opacity: 0 }} whileInView={{ opacity: 1 }} viewport={{ once: true }} className="text-center">
            <div className="inline-flex items-center gap-3 px-5 py-2.5 bg-white/[0.03] border border-white/[0.08] rounded-full">
              <div className="flex -space-x-1.5">
                {['A', 'B', 'C', 'D', 'E'].map((l, i) => (
                  <div key={i} className="w-6 h-6 bg-gradient-to-br from-emerald-400 to-teal-500 rounded-full border-2 border-[#08090c] flex items-center justify-center text-[9px] font-bold">{l}</div>
                ))}
              </div>
              <span className="text-xs text-slate-400"><span className="text-emerald-400 font-bold">23 people</span> joined today</span>
            </div>
          </motion.div>
        </div>
      </section>

      {/* ═══ BLOG ═══ */}
      {blogPosts.length > 0 && (
        <section className="relative py-24 px-4 z-10 border-t border-white/[0.05]">
          <div className="max-w-6xl mx-auto">
            <motion.div initial={{ opacity: 0 }} whileInView={{ opacity: 1 }} viewport={{ once: true }} className="text-center mb-12">
              <span className="inline-flex items-center gap-2 px-4 py-2 bg-white/[0.04] border border-white/[0.08] rounded-full text-slate-400 text-xs font-mono uppercase tracking-wider mb-6">
                <BookOpen className="w-3.5 h-3.5 text-emerald-400" />
                Career Blog
              </span>
              <h2 className="text-4xl md:text-5xl font-extrabold tracking-tight mb-4">Latest Insights</h2>
              <p className="text-slate-400 max-w-2xl mx-auto text-sm">Expert advice on career growth, job search, and professional development</p>
            </motion.div>

            <div className="grid md:grid-cols-3 gap-5 mb-10">
              {blogPosts.map((post, index) => (
                <motion.div key={post.id} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: index * 0.1 }}>
                  <Link to={`/blog/${post.slug}`} className="block h-full p-6 bg-[#0d0f14] border border-white/[0.06] rounded-2xl hover:border-emerald-500/20 transition-all group">
                    <div className="flex items-center gap-2 mb-3">
                      <span className="px-2 py-0.5 bg-emerald-500/10 border border-emerald-500/20 rounded text-[10px] font-mono text-emerald-400 capitalize">{post.category?.replace('-', ' ') || 'Career'}</span>
                      <span className="text-[10px] text-slate-500 flex items-center gap-1 font-mono"><Clock className="w-2.5 h-2.5" />{post.read_time_minutes}m</span>
                    </div>
                    <h3 className="text-[15px] font-bold text-white mb-2 group-hover:text-emerald-400 transition-colors line-clamp-2">{post.title}</h3>
                    <p className="text-xs text-slate-400 line-clamp-2 mb-4">{post.excerpt}</p>
                    <span className="text-emerald-400 text-xs font-medium flex items-center gap-1 group-hover:gap-2 transition-all">
                      Read More <ArrowRight className="w-3 h-3" />
                    </span>
                  </Link>
                </motion.div>
              ))}
            </div>

            <div className="text-center">
              <Link to="/blog" className="inline-flex items-center gap-2 px-5 py-2.5 bg-white/[0.04] border border-white/[0.08] rounded-xl text-sm font-medium hover:bg-white/[0.08] transition-colors">
                View All Articles <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
          </div>
        </section>
      )}

      {/* ═══ FAQ ═══ */}
      <section className="relative py-24 px-4 z-10 border-t border-white/[0.05]">
        <div className="max-w-3xl mx-auto">
          <motion.div initial={{ opacity: 0 }} whileInView={{ opacity: 1 }} viewport={{ once: true }} className="text-center mb-14">
            <h2 className="text-4xl md:text-5xl font-extrabold tracking-tight mb-3">Questions? Answers.</h2>
            <p className="text-slate-400 text-sm">Everything you need to know about Rastor AI</p>
          </motion.div>
          <FAQ items={faqs} />
        </div>
      </section>

      {/* ═══ FINAL CTA ═══ */}
      <section className="relative py-28 px-4 z-10">
        <motion.div initial={{ opacity: 0, y: 40 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} className="max-w-4xl mx-auto">
          <div className="relative p-12 md:p-16 bg-gradient-to-br from-emerald-500/[0.08] via-transparent to-teal-500/[0.08] border border-emerald-500/[0.15] rounded-3xl text-center overflow-hidden">
            <div className="absolute top-0 right-0 w-80 h-80 bg-emerald-500/[0.08] rounded-full blur-[120px] pointer-events-none" />
            <div className="absolute bottom-0 left-0 w-60 h-60 bg-teal-500/[0.08] rounded-full blur-[100px] pointer-events-none" />

            <div className="relative z-10">
              <h2 className="text-4xl md:text-6xl font-extrabold text-white mb-5 tracking-tight">
                Your Dream Career
                <br />Starts Here
              </h2>
              <p className="text-lg text-slate-400 mb-10 max-w-xl mx-auto">
                10,847 professionals already made the switch. Your turn.
              </p>
              <div className="flex flex-col sm:flex-row items-center justify-center gap-4 mb-8">
                <motion.button
                  whileHover={{ scale: 1.04, boxShadow: '0 0 40px rgba(16,185,129,0.3)' }}
                  whileTap={{ scale: 0.97 }}
                  onClick={() => navigate('/build-roadmap')}
                  className="px-10 py-5 bg-gradient-to-r from-emerald-500 to-teal-500 text-white text-lg font-bold rounded-2xl shadow-xl shadow-emerald-500/25 flex items-center gap-3"
                >
                  <Rocket className="w-5 h-5" />
                  <span>Get Started Free</span>
                </motion.button>
                <Link to="/resume-builder" className="px-10 py-5 bg-white/[0.06] border border-white/[0.12] text-white text-lg font-semibold rounded-2xl flex items-center gap-3 hover:bg-white/[0.1] transition-colors">
                  <FileText className="w-5 h-5" />
                  <span>Build Resume</span>
                </Link>
              </div>
              <p className="text-slate-500 text-sm">No credit card required • 2 minute setup</p>
            </div>
          </div>
        </motion.div>
      </section>

      <div className="h-16" />
    </div>
  );
};

export default HomePage;
